// VOY-417 §8 — Memory Editor (VOY-458 UI, absorbed into this ticket).
//
// VOY-458 shipped the backend (confirm/correct/pin/delete every fact) +
// types + API helpers; the UI was deferred and orphaned. This section
// is that UI.
//
// Data source: `AgentContext.resolved_preferences` — each entry is a
// `ResolvedPreference` carrying:
//   value, source (override|confirmed|declared|quiz|behavioral),
//   confidence, pinned, confirmed_count, last_confirmed_at
// This is the canonical view of "what Voy knows about you" because it's
// the same data the resolver uses to feed the agent. Every dim VOY-458's
// endpoints operate on appears here.
//
// Each row exposes the four VOY-458 actions:
//   - Confirm  (POST /memory/confirm)  — endorse current value, no change
//   - Correct  (POST /memory/correct)  — replace value, tombstone prior
//   - Pin      (POST /memory/pin)      — lock without changing
//   - Delete   (POST /memory/delete)   — tombstone + remove override
//
// All four go through useFactCorrection — the SAME flow that powers §3's
// per-attribute Correct. One backend, one shared client-side state.

import { useState } from 'react'
import { Loader2, Check, Pencil, Pin, Trash2 } from 'lucide-react'
import type { ResolvedPreferences, ResolvedPreference, ResolutionSource } from '@/types/profile'
import { type FactCorrectionApi } from './useFactCorrection'
import { CorrectionModal } from './CorrectionModal'
import { asString } from './utils'

export interface MemoryEditorSectionProps {
  resolvedPreferences: ResolvedPreferences | null
  loading: boolean
  correction: FactCorrectionApi
}

const DIMENSION_LABEL: Record<string, string> = {
  // 8 quiz dimensions
  adventure_vs_comfort: 'Adventure vs comfort',
  planned_vs_spontaneous: 'Planned vs spontaneous',
  budget_vs_luxury: 'Budget vs luxury',
  solo_vs_social: 'Solo vs social',
  fast_vs_slow: 'Fast vs slow',
  familiar_vs_novel: 'Familiar vs novel',
  urban_vs_nature: 'Urban vs nature',
  food_centrality: 'Food centrality',
  // categorical
  budget_level: 'Budget level',
  pace: 'Pace',
  accommodation: 'Accommodation',
  group_type: 'Group type',
  planning_style: 'Planning style',
  home_base: 'Home base',
  dietary: 'Dietary',
  accessibility: 'Accessibility',
}

const SOURCE_LABEL: Record<ResolutionSource, string> = {
  override: 'OVERRIDE',
  confirmed: 'CONFIRMED',
  declared: 'DECLARED',
  quiz: 'QUIZ',
  behavioral: 'INFERRED',
}

function sourceClass(s: ResolutionSource | null | undefined): string {
  if (s === 'override' || s === 'confirmed') return 'pill mint'
  if (s === 'declared') return 'pill purple'
  if (s === 'quiz') return 'pill accent'
  return 'pill'
}

interface ResolvedRow {
  dimension: string
  pref: ResolvedPreference
}

function toRows(rp: ResolvedPreferences | null): ResolvedRow[] {
  if (!rp) return []
  return (Object.entries(rp) as Array<[string, ResolvedPreference]>)
    // Show any dim that has signal — a value, a confirm, or a pin.
    .filter(([, pref]) => pref.value !== null || (pref.confirmed_count ?? 0) > 0 || pref.pinned)
    // Pinned + corrected first (most user-touched), then by confidence desc.
    .sort(([, a], [, b]) => {
      const pa = (a.pinned ? 2 : 0) + ((a.confirmed_count ?? 0) > 0 ? 1 : 0)
      const pb = (b.pinned ? 2 : 0) + ((b.confirmed_count ?? 0) > 0 ? 1 : 0)
      if (pa !== pb) return pb - pa
      return (b.confidence ?? 0) - (a.confidence ?? 0)
    })
    .map(([dimension, pref]) => ({ dimension, pref }))
}

export function MemoryEditorSection({ resolvedPreferences, loading, correction }: MemoryEditorSectionProps) {
  const [activeCorrect, setActiveCorrect] = useState<string | null>(null)
  // Inline confirm state for Delete — replaces window.confirm() (bot
  // review #8) so the destructive prompt matches v6 chrome and isn't
  // suppressed by browser modal-blockers.
  const [pendingDelete, setPendingDelete] = useState<string | null>(null)
  const rows = toRows(resolvedPreferences)
  const isEmpty = !loading && rows.length === 0

  function openCorrect(dimension: string) {
    setActiveCorrect(dimension)
  }
  function closeCorrect() {
    setActiveCorrect(null)
    correction.resetError()
  }

  const activeRow = activeCorrect ? rows.find((r) => r.dimension === activeCorrect) ?? null : null

  async function handleConfirm(row: ResolvedRow) {
    const value = row.pref.value
    await correction.confirm(row.dimension, value ?? undefined)
  }
  async function handlePin(row: ResolvedRow) {
    const value = row.pref.value
    if (value == null) return  // backend requires a value when no prior override
    await correction.pin(row.dimension, value)
  }
  function requestDelete(row: ResolvedRow) {
    setPendingDelete(row.dimension)
  }
  function cancelDelete() {
    setPendingDelete(null)
  }
  async function confirmDelete(row: ResolvedRow) {
    setPendingDelete(null)
    await correction.remove(row.dimension, { currentValue: row.pref.value ?? undefined })
  }
  async function handleCorrect(newValue: string, reason: string) {
    if (!activeRow) return
    const ok = await correction.correct(activeRow.dimension, newValue, {
      reason: reason || undefined,
      priorValue: activeRow.pref.value ?? undefined,
    })
    if (ok) closeCorrect()
  }
  async function handlePinFromModal() {
    if (!activeRow) return
    const ok = await correction.pin(activeRow.dimension, activeRow.pref.value ?? undefined)
    if (ok) closeCorrect()
  }

  return (
    <section className="card" aria-labelledby="editor-h">
      <div className="card-h">
        <h3 id="editor-h">Memory Editor &mdash; <span className="serif-i">what Voy knows about you</span></h3>
        <span className="meta">{loading ? 'LOADING…' : `${rows.length} FACT${rows.length === 1 ? '' : 'S'}`}</span>
      </div>

      <p className="you-editor-blurb">
        Every fact Voy infers or you&apos;ve declared. <strong>Confirm</strong> a fact to lock it in,{' '}
        <strong>Correct</strong> a wrong one, <strong>Pin</strong> to lock without changing, or{' '}
        <strong>Delete</strong> to drop it (and tell the agent never to re-derive it).
      </p>

      {isEmpty ? (
        <div className="you-empty-state">
          <p className="you-empty-headline">No facts yet.</p>
          <p className="you-empty-sub">
            As you chat, take the quiz, or book, facts show up here. Each one is correctable.
          </p>
        </div>
      ) : (
        <ul className="you-fact-list">
          {rows.map((row) => {
            const isPending =
              correction.state.saving
              && correction.state.pendingDimension === row.dimension
            const isPendingDelete = pendingDelete === row.dimension
            return (
              <li key={row.dimension} className="you-fact-row">
                <div className="you-fact-body">
                  <div className="you-fact-head">
                    <span className="you-fact-label">{labelFor(row.dimension)}</span>
                    <span className={sourceClass(row.pref.source)}>
                      {row.pref.source ? SOURCE_LABEL[row.pref.source] : '—'}
                    </span>
                    {row.pref.pinned && <span className="pill accent">PINNED</span>}
                    {(row.pref.confirmed_count ?? 0) > 0 && (
                      <span className="pill mint">
                        ✓ {row.pref.confirmed_count}× confirmed
                      </span>
                    )}
                  </div>
                  <div className="you-fact-value">{asString(row.pref.value)}</div>
                  {isPendingDelete && (
                    <div className="you-fact-confirm" role="alert">
                      <span>
                        Delete this fact? Voy will stop remembering &quot;{asString(row.pref.value)}&quot; and
                        treat any future re-derivation as a negative signal.
                      </span>
                      <div className="row" style={{ gap: 6 }}>
                        <button type="button" className="you-fact-action" onClick={cancelDelete}>Cancel</button>
                        <button type="button" className="you-fact-action danger" onClick={() => void confirmDelete(row)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="you-fact-actions">
                  {isPending ? (
                    <Loader2 className="kw-spin" aria-hidden="true" />
                  ) : !isPendingDelete ? (
                    <>
                      <button type="button" className="you-fact-action" onClick={() => void handleConfirm(row)} title="Confirm — endorse this fact" disabled={correction.state.saving}>
                        <Check aria-hidden="true" /> Confirm
                      </button>
                      <button type="button" className="you-fact-action" onClick={() => openCorrect(row.dimension)} title="Correct — replace this value" disabled={correction.state.saving}>
                        <Pencil aria-hidden="true" /> Correct
                      </button>
                      <button type="button" className="you-fact-action" onClick={() => void handlePin(row)} title="Pin — lock without changing" disabled={correction.state.saving || row.pref.value == null}>
                        <Pin aria-hidden="true" /> Pin
                      </button>
                      <button type="button" className="you-fact-action danger" onClick={() => requestDelete(row)} title="Delete — tombstone this fact" disabled={correction.state.saving}>
                        <Trash2 aria-hidden="true" /> Delete
                      </button>
                    </>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {correction.state.error && !activeRow && (
        <p style={{ color: 'var(--v6-neg)', fontSize: 12, marginTop: 10 }} role="alert">
          {correction.state.error}
        </p>
      )}

      {activeRow && (
        <CorrectionModal
          open
          onClose={closeCorrect}
          dimension={activeRow.dimension}
          dimensionLabel={labelFor(activeRow.dimension)}
          currentValue={activeRow.pref.value}
          saving={correction.state.saving}
          error={correction.state.error}
          onCorrect={handleCorrect}
          onPin={handlePinFromModal}
        />
      )}
    </section>
  )
}

function labelFor(dim: string): string {
  return DIMENSION_LABEL[dim] ?? dim
}
